import { createRouter, createWebHistory, type RouteRecordRaw } from 'vue-router'
import { useAuthStore } from '@/store/auth'

const routes: RouteRecordRaw[] = [
  {
    path: '/login',
    name: 'login',
    component: () => import('@/views/Login.vue'),
    meta: { layout: 'blank', title: 'nav.login' },
  },
  {
    path: '/register',
    name: 'register',
    component: () => import('@/views/Register.vue'),
    meta: { layout: 'blank', title: 'register.title' },
  },
  {
    path: '/',
    name: 'dashboard',
    component: () => import('@/views/Dashboard.vue'),
    // Arco Pro's workplace has no page heading and paints its own panels.
    meta: { title: 'nav.dashboard', bare: true },
  },
  {
    path: '/domains',
    name: 'domains',
    component: () => import('@/views/Domains.vue'),
    meta: { title: 'nav.domains', ownSurface: true },
  },
  {
    path: '/domains/:id',
    name: 'domain-detail',
    component: () => import('@/views/DomainDetail.vue'),
    // A heading of its own (DomainHeader) and its own panels.
    meta: { title: 'nav.domain', bare: true },
  },
  {
    path: '/domains/:id/rules',
    name: 'rules',
    component: () => import('@/views/Rules.vue'),
    meta: { title: 'rules.title', bare: true },
  },
  {
    path: '/certificates',
    name: 'certificates',
    component: () => import('@/views/Certificates.vue'),
    meta: { title: 'certificates.title', ownSurface: true },
  },
  {
    path: '/security',
    name: 'security-events',
    component: () => import('@/views/SecurityEvents.vue'),
    meta: { title: 'security.title', ownSurface: true },
  },
  // 攻击事件's old address.
  { path: '/security/attacks', redirect: { name: 'security-events' } },
  {
    path: '/packages/buy',
    name: 'package-store',
    component: () => import('@/views/PackageStore.vue'),
    meta: { title: 'store.title', ownSurface: true },
  },
  {
    path: '/packages',
    name: 'my-packages',
    component: () => import('@/views/MyPackages.vue'),
    meta: { title: 'myPackages.title', ownSurface: true },
  },
  // 财务管理. 充值中心 keeps /billing: Alipay's cashier comes back there,
  // /billing?topup=<order id>.
  {
    path: '/billing',
    name: 'billing',
    component: () => import('@/views/Billing.vue'),
    meta: { title: 'billing.title', ownSurface: true },
  },
  {
    path: '/finance/ledger',
    name: 'finance-ledger',
    component: () => import('@/views/FinanceLedger.vue'),
    meta: { title: 'ledger.title', ownSurface: true },
  },
  {
    path: '/finance/orders',
    name: 'finance-orders',
    component: () => import('@/views/FinanceOrders.vue'),
    meta: { title: 'orders.title', ownSurface: true },
  },
  {
    path: '/finance/withdraw',
    name: 'finance-withdraw',
    component: () => import('@/views/FinanceWithdraw.vue'),
    meta: { title: 'withdraw.title', ownSurface: true },
  },
  {
    path: '/finance/invoices',
    name: 'finance-invoices',
    component: () => import('@/views/FinanceInvoices.vue'),
    meta: { title: 'invoice.title', ownSurface: true },
  },
  {
    path: '/finance/refunds',
    name: 'finance-refunds',
    component: () => import('@/views/FinanceRefunds.vue'),
    meta: { title: 'refund.title', ownSurface: true },
  },
  // 工单中心. A ticket's id is digits, so /tickets/new is never one.
  // 消息中心 and 消息接收配置: the bell's 查看更多 and its settings.
  {
    path: '/messages',
    name: 'messages',
    component: () => import('@/views/MessageCenter.vue'),
    meta: { title: 'inbox.title', ownSurface: true },
  },
  {
    path: '/messages/settings',
    name: 'message-settings',
    component: () => import('@/views/MessageSettings.vue'),
    meta: { title: 'inbox.settingsTitle', ownSurface: true },
  },
  {
    path: '/tickets',
    name: 'tickets',
    component: () => import('@/views/Tickets.vue'),
    meta: { title: 'tickets.title', ownSurface: true },
  },
  // 提交工单 is a drawer on 我的工单 now; the old address opens it there.
  { path: '/tickets/new', redirect: (to) => ({ name: 'tickets', query: { ...to.query, new: '1' } }) },
  {
    path: '/tickets/:id(\\d+)',
    name: 'ticket-detail',
    component: () => import('@/views/TicketDetail.vue'),
    // A heading of its own: the ticket's title, state and facts.
    meta: { title: 'tickets.detailTitle', bare: true },
  },
  // 应用加速.
  {
    path: '/l4-rules',
    name: 'l4-rules',
    component: () => import('@/views/L4Rules.vue'),
    meta: { title: 'l4.title', ownSurface: true },
  },
  // 我的投诉.
  {
    path: '/complaints',
    name: 'complaints',
    component: () => import('@/views/Complaints.vue'),
    meta: { title: 'complaints.title', ownSurface: true },
  },
  // 账户中心: the account's own facts, a tab each — 基础信息 (avatar,
  // nickname and the details its owner gives), 实名认证 (?tab=realname)
  // and 会员等级 (?tab=member).
  {
    path: '/account',
    name: 'account-center',
    component: () => import('@/views/AccountCenter.vue'),
    meta: { title: 'accountCenter.title', ownSurface: true },
  },
  // The pages it took in, at their old addresses and names — links,
  // bookmarks and the search (under the tabs' names) open their tab.
  {
    path: '/account/profile',
    name: 'account-profile',
    redirect: { name: 'account-center' },
    meta: { title: 'accountCenter.basic' },
  },
  {
    path: '/account/member',
    name: 'account-member',
    redirect: { name: 'account-center', query: { tab: 'member' } },
    meta: { title: 'accountCenter.member' },
  },
  // 安全中心: the signed-in account's own password, MFA device, 通行密钥,
  // sessions and sign-ins — or a 子用户's own password.
  {
    path: '/account/security',
    name: 'account-security',
    component: () => import('@/views/AccountSecurity.vue'),
    meta: { title: 'securityCenter.title', ownSurface: true },
  },
  // 子用户: the account's other sign-ins (the main account only).
  {
    path: '/account/sub-users',
    name: 'sub-users',
    component: () => import('@/views/SubUsers.vue'),
    meta: { title: 'subUsers.title', ownSurface: true, mainAccount: true },
  },
  // 实名认证, now 账户中心's tab; and where the Alipay app sends people
  // back to — signed in or not (a phone's Alipay window is signed in to
  // nothing here).
  {
    path: '/realname',
    name: 'realname',
    redirect: { name: 'account-center', query: { tab: 'realname' } },
    meta: { title: 'accountCenter.realname' },
  },
  {
    path: '/realname/done',
    name: 'realname-done',
    component: () => import('@/views/RealnameDone.vue'),
    meta: { layout: 'blank', title: 'realname.doneTitle' },
  },
  {
    path: '/credentials',
    name: 'credentials',
    component: () => import('@/views/Credentials.vue'),
    meta: { title: 'credentials.title', ownSurface: true },
  },
  {
    path: '/ip-lists',
    name: 'ip-lists',
    component: () => import('@/views/IPLists.vue'),
    meta: { title: 'ipLists.title', ownSurface: true },
  },
  { path: '/:pathMatch(.*)*', redirect: { name: 'dashboard' } },
]

const router = createRouter({
  history: createWebHistory(),
  routes,
})

router.beforeEach(async (to) => {
  const auth = useAuthStore()
  if (to.name === 'login' || to.name === 'register' || to.name === 'realname-done') return true
  if (!auth.isLoggedIn) return { name: 'login', query: { next: to.fullPath } }
  if (!auth.user) await auth.refreshMe()
  // Still signed in with no account: /me did not answer. Fail the
  // navigation rather than show the console without its account; the first
  // one failing puts a reload on the boot screen (bootFailed).
  if (auth.isLoggedIn && !auth.user) throw new Error('the account could not be loaded')
  // The main account's own pages (子用户): a sub-user goes to the overview.
  if (to.meta.mainAccount && auth.user?.sub_user) return { name: 'dashboard' }
  return true
})

export default router
